import { Type } from '@angular/core';
import { PrivacyDialogsFastTrackComponent } from '@client/app/modules/data-privacy/dialogs/fast-track/privacy-dialogs-fast-track.component';

export type SideNavigationProfile = 'domains' | 'admin';

export type SideNavigationVisibilityKey =
    | 'prioritized-content'
    | 'accessibility-root'
    | 'accessibility-pdfs'
    | 'accessibility-modern'
    | 'accessibility-legacy'
    | 'accessibility-pdfs-modern'
    | 'accessibility-pdfs-legacy'
    | 'quality-root'
    | 'quality-summary'
    | 'quality-policies'
    | 'quality-pagecorrect'
    | 'quality-pagecorrect-accessibility-fixes'
    | 'quality-spellcheck'
    | 'quality-links'
    | 'quality-readability'
    | 'quality-language'
    | 'insights-root'
    | 'site-performance-root'
    | 'privacy-root'
    | 'statistics-root'
    | 'uptime-root'
    | 'history-center'
    | 'inventory-root'
    | 'seo-root'
    | 'performance-root'
    | 'data-privacy-core'
    | 'consent-manager-root'
    | 'consent-manager-cookie-overview'
    | 'admin-labels'
    | 'admin-audit-log'
    | 'admin-source-code-excludes'
    | 'admin-api-users'
    | 'admin-page-performance'
    | 'admin-jira-connector';

export interface SideNavigationItem {
    id: string;
    label: string;
    icon?: string;
    route?: string;
    routeParams?: Record<string, unknown>;
    dialogParams?: {
        body: Type<unknown>;
        size?: string;
    };
    activeRoutes?: string[];
    routeMatchers?: string[];
    visibilityKey?: SideNavigationVisibilityKey;
    children?: SideNavigationItem[];
    dividerAfter?: boolean;
}

export const DOMAIN_SIDE_NAV_ITEMS: SideNavigationItem[] = [
    {
        id: 'dashboard',
        label: 'navigation.sideNavigation.dashboard',
        icon: 'dashboard',
        route: 'base.customer.domain.dashboard',
        activeRoutes: ['base.dashboard', 'base.customer.domain.dashboard'],
    },
    {
        id: 'prioritized-pages',
        label: 'navigation.sideNavigation.prioritizedPages',
        icon: 'prioritizedPages',
        route: 'base.customer.domain.pages',
        visibilityKey: 'prioritized-content',
        dividerAfter: true,
    },
    {
        id: 'accessibility',
        label: 'navigation.sideNavigation.accessibility',
        icon: 'accessibility',
        route: 'base.customer.domain.accessibility.summary',
        visibilityKey: 'accessibility-root',
        children: [
            {
                id: 'accessibility-summary',
                label: 'navigation.sideNavigation.summary',
                route: 'base.customer.domain.accessibility.summary',
            },
            {
                id: 'accessibility-issues-by-type',
                label: 'navigation.sideNavigation.issuesByType',
                route: 'base.customer.domain.accessibility.issues-by-type',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.issues-by-type',
                    'base.customer.domain.accessibility.affected-pages',
                    'base.customer.domain.accessibility.affected-page-by-issue',
                ],
            },
            {
                id: 'accessibility-issues-by-page',
                label: 'navigation.sideNavigation.issuesByPage',
                route: 'base.customer.domain.accessibility.issues-by-page',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.issues-by-page',
                    'base.customer.domain.accessibility.page-checks',
                    'base.customer.domain.accessibility.code-snippets-by-page',
                ],
            },
            {
                id: 'accessibility-pages',
                label: 'navigation.sideNavigation.pagesWithFailingChecks',
                route: 'base.customer.domain.accessibility.pages',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-checklist',
                label: 'navigation.sideNavigation.checklist',
                route: 'base.customer.domain.accessibility.issues',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-guidelines',
                label: 'navigation.sideNavigation.guidelines',
                route: 'base.customer.domain.accessibility.guidelines',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-ignored-pages',
                label: 'navigation.sideNavigation.pagesWithIgnoredChecks',
                route: 'base.customer.domain.accessibility.ignored-pages',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-fast-track',
                label: 'navigation.sideNavigation.fastTrack',
                route: 'base.customer.domain.accessibility.fast-track',
                visibilityKey: 'accessibility-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.fast-track',
                    'base.customer.domain.accessibility.code-snippet',
                    'base.customer.domain.accessibility.fast-track-check',
                ],
            },
            {
                id: 'accessibility-pdfs',
                label: 'navigation.sideNavigation.pdfs',
                route: 'base.customer.domain.accessibility.pdfs',
                visibilityKey: 'accessibility-pdfs-modern',
                activeRoutes: [
                    'base.customer.domain.accessibility.pdfs',
                    'base.customer.domain.accessibility.pdfs-report',
                    'base.customer.domain.accessibility.pdfs-report-checks',
                ],
            },
            {
                id: 'accessibility-fast-track',
                label: 'navigation.sideNavigation.fastTrack',
                route: 'base.customer.domain.accessibility.fasttrack',
                visibilityKey: 'accessibility-legacy',
            },
            {
                id: 'accessibility-pdfs',
                label: 'navigation.sideNavigation.pdfs',
                route: 'base.customer.domain.accessibility.internal-pdf',
                visibilityKey: 'accessibility-pdfs-legacy',
                activeRoutes: [
                    'base.customer.domain.accessibility.internal-pdf',
                    'base.customer.domain.accessibility.internal-pdf-accessibility',
                    'base.customer.domain.accessibility.external-pdf',
                ],
                children: [
                    {
                        id: 'accessibility-pdfs-internal',
                        label: 'navigation.sideNavigation.internalPdfs',
                        route: 'base.customer.domain.accessibility.internal-pdf',
                        activeRoutes: [
                            'base.customer.domain.accessibility.internal-pdf',
                            'base.customer.domain.accessibility.internal-pdf-accessibility',
                        ],
                    },
                    {
                        id: 'accessibility-pdfs-external',
                        label: 'navigation.sideNavigation.externalPdfs',
                        route: 'base.customer.domain.accessibility.external-pdf',
                    },
                ],
            },
        ],
    },
    {
        id: 'content-quality',
        label: 'navigation.sideNavigation.contentQuality',
        icon: 'contentQuality',
        route: 'base.customer.domain.qa.summary',
        visibilityKey: 'quality-root',
        children: [
            {
                id: 'content-quality-summary',
                label: 'navigation.sideNavigation.summary',
                route: 'base.customer.domain.qa.summary',
                visibilityKey: 'quality-summary',
            },
            {
                id: 'content-quality-pages',
                label: 'navigation.sideNavigation.issuesByPage',
                route: 'base.customer.domain.qa.pages',
                visibilityKey: 'quality-summary',
            },
            {
                id: 'content-quality-links',
                label: 'navigation.sideNavigation.brokenContent',
                route: 'base.customer.domain.qa.broken.links',
                visibilityKey: 'quality-links',
                activeRoutes: [
                    'base.customer.domain.qa.broken.links',
                    'base.customer.domain.qa.broken.images',
                    'base.customer.domain.qa.broken.pages',
                    'base.customer.domain.qa.broken.sitemap',
                ],
                children: [
                    {
                        id: 'content-quality-links-pages',
                        label: 'navigation.sideNavigation.brokenContentByPage',
                        route: 'base.customer.domain.qa.broken.pages',
                    },
                    {
                        id: 'content-quality-links-links',
                        label: 'navigation.sideNavigation.brokenLinks',
                        route: 'base.customer.domain.qa.broken.links',
                    },
                    {
                        id: 'content-quality-links-images',
                        label: 'navigation.sideNavigation.brokenImages',
                        route: 'base.customer.domain.qa.broken.images',
                    },
                    {
                        id: 'content-quality-links-sitemap',
                        label: 'navigation.sideNavigation.brokenSitemapLinks',
                        route: 'base.customer.domain.qa.broken.sitemap',
                    },
                ],
            },
            {
                id: 'content-quality-spellcheck',
                label: 'navigation.sideNavigation.misspellings',
                route: 'base.customer.domain.qa.spellcheck.index',
                visibilityKey: 'quality-spellcheck',
                activeRoutes: [
                    'base.customer.domain.qa.spellcheck.index',
                    'base.customer.domain.qa.spellcheck.pages',
                    'base.customer.domain.qa.spellcheck.potential',
                    'base.customer.domain.qa.spellcheck.confirmed',
                    'base.customer.domain.qa.spellcheck.dictionary',
                    'base.customer.domain.qa.spellcheck.ignored-misspellings',
                    'base.customer.domain.qa.spellcheck.qa-errors',
                    'base.customer.domain.qa.spellcheck.dashboard',
                ],
                children: [
                    {
                        id: 'content-quality-spellcheck-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.qa.spellcheck.dashboard',
                    },
                    {
                        id: 'content-quality-spellcheck-pages',
                        label: 'navigation.sideNavigation.misspellingsByPage',
                        route: 'base.customer.domain.qa.spellcheck.pages',
                    },
                    {
                        id: 'content-quality-spellcheck-misspellings',
                        label: 'navigation.sideNavigation.misspellings',
                        route: 'base.customer.domain.qa.spellcheck.confirmed',
                    },
                    {
                        id: 'content-quality-spellcheck-potential',
                        label: 'navigation.sideNavigation.potentialMisspellings',
                        route: 'base.customer.domain.qa.spellcheck.potential',
                    },
                    {
                        id: 'content-quality-spellcheck-dictionary',
                        label: 'navigation.sideNavigation.dictionary',
                        route: 'base.customer.domain.qa.spellcheck.dictionary',
                    },
                    {
                        id: 'content-quality-spellcheck-ignored',
                        label: 'navigation.sideNavigation.ignoredMisspellings',
                        route: 'base.customer.domain.qa.spellcheck.ignored-misspellings',
                    },
                ],
            },
            {
                id: 'content-quality-readability',
                label: 'navigation.sideNavigation.readability',
                route: 'base.customer.domain.qa.readability.dashboard',
                visibilityKey: 'quality-readability',
                activeRoutes: [
                    'base.customer.domain.qa.readability.dashboard',
                    'base.customer.domain.qa.readability.checker',
                ],
                children: [
                    {
                        id: 'content-quality-readability-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.qa.readability.dashboard',
                    },
                    {
                        id: 'content-quality-readability-checker',
                        label: 'navigation.sideNavigation.readabilityChecker',
                        route: 'base.customer.domain.qa.readability.checker',
                    },
                ],
            },
            {
                id: 'content-quality-language',
                label: 'navigation.sideNavigation.languageValidation',
                route: 'base.customer.domain.qa.languageMatch.summary',
                visibilityKey: 'quality-language',
                activeRoutes: [
                    'base.customer.domain.qa.languageMatch.summary',
                    'base.customer.domain.qa.languageMatch.pages',
                ],
                children: [
                    {
                        id: 'content-quality-language-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.qa.languageMatch.summary',
                    },
                    {
                        id: 'content-quality-language-pages',
                        label: 'navigation.sideNavigation.issuesByPage',
                        route: 'base.customer.domain.qa.languageMatch.pages',
                    },
                ],
            },
            {
                id: 'content-quality-policies',
                label: 'navigation.sideNavigation.contentPolicies',
                route: 'base.customer.domain.policy.dashboard',
                visibilityKey: 'quality-policies',
                activeRoutes: [
                    'base.customer.domain.policy.dashboard',
                    'base.customer.domain.policy.pages',
                    'base.customer.domain.policy.policies',
                    'base.customer.domain.policy.ignored-pages',
                ],
                children: [
                    {
                        id: 'content-quality-policies-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.policy.dashboard',
                    },
                    {
                        id: 'content-quality-policies-pages',
                        label: 'navigation.sideNavigation.issuesByPage',
                        route: 'base.customer.domain.policy.pages',
                    },
                    {
                        id: 'content-quality-policies-list',
                        label: 'navigation.sideNavigation.policiesList',
                        route: 'base.customer.domain.policy.policies',
                    },
                    {
                        id: 'content-quality-policies-ignored',
                        label: 'navigation.sideNavigation.pagesWithIgnoredPolicies',
                        route: 'base.customer.domain.policy.ignored-pages',
                    },
                ],
            },
            {
                id: 'content-quality-pagecorrect',
                label: 'navigation.sideNavigation.pageCorrect',
                route: 'base.customer.domain.page-fix.summary',
                visibilityKey: 'quality-pagecorrect',
                activeRoutes: [
                    'base.customer.domain.page-fix.summary',
                    'base.customer.domain.page-fix.index',
                    'base.customer.domain.page-fix.checks',
                ],
                children: [
                    {
                        id: 'content-quality-pagecorrect-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.page-fix.summary',
                    },
                    {
                        id: 'content-quality-pagecorrect-list-of-fixes',
                        label: 'navigation.sideNavigation.listOfFixes',
                        route: 'base.customer.domain.page-fix.index',
                    },
                    {
                        id: 'content-quality-pagecorrect-accessibility-fixes',
                        label: 'navigation.sideNavigation.accessibilityFixes',
                        route: 'base.customer.domain.page-fix.checks',
                        visibilityKey: 'quality-pagecorrect-accessibility-fixes',
                    },
                ],
            },
        ],
    },
    {
        id: 'insights',
        label: 'navigation.sideNavigation.insights',
        icon: 'insights',
        route: 'base.customer.domain.statistics.summary',
        visibilityKey: 'insights-root',
        children: [
            {
                id: 'insights-site-analytics',
                label: 'navigation.sideNavigation.siteAnalytics',
                route: 'base.customer.domain.statistics.summary',
                visibilityKey: 'statistics-root',
                routeMatchers: ['base.customer.domain.statistics'],
                children: [
                    {
                        id: 'insights-site-analytics-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.statistics.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.summary',
                        ],
                    },
                    {
                        id: 'insights-site-analytics-visitors',
                        label: 'navigation.sideNavigation.visitors',
                        route: 'base.customer.domain.statistics.visitors.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.visitors.summary',
                            'base.customer.domain.statistics.visitors.locations',
                            'base.customer.domain.statistics.visitors.locations.index',
                            'base.customer.domain.statistics.visitors.locations.regions',
                            'base.customer.domain.statistics.visitors.locations.cities',
                            'base.customer.domain.statistics.visitors.language',
                            'base.customer.domain.statistics.visitors.organizations',
                            'base.customer.domain.statistics.visitors.devices',
                            'base.customer.domain.statistics.visitors.browsers',
                            'base.customer.domain.statistics.visitors.os',
                            'base.customer.domain.statistics.visitors.resolutions',
                            'base.customer.domain.statistics.visitors.log',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-visitors-summary',
                                label: 'navigation.sideNavigation.visitorsSummary',
                                route: 'base.customer.domain.statistics.visitors.summary',
                            },
                            {
                                id: 'insights-site-analytics-visitors-locations',
                                label: 'navigation.sideNavigation.locations',
                                route: 'base.customer.domain.statistics.visitors.locations.index',
                                activeRoutes: [
                                    'base.customer.domain.statistics.visitors.locations.index',
                                    'base.customer.domain.statistics.visitors.locations.regions',
                                    'base.customer.domain.statistics.visitors.locations.cities',
                                ],
                            },
                            {
                                id: 'insights-site-analytics-visitors-language',
                                label: 'navigation.sideNavigation.languages',
                                route: 'base.customer.domain.statistics.visitors.language',
                            },
                            {
                                id: 'insights-site-analytics-visitors-organizations',
                                label: 'navigation.sideNavigation.isps',
                                route: 'base.customer.domain.statistics.visitors.organizations',
                            },
                            {
                                id: 'insights-site-analytics-visitors-devices',
                                label: 'navigation.sideNavigation.devices',
                                route: 'base.customer.domain.statistics.visitors.devices',
                            },
                            {
                                id: 'insights-site-analytics-visitors-browsers',
                                label: 'navigation.sideNavigation.browsers',
                                route: 'base.customer.domain.statistics.visitors.browsers',
                            },
                            {
                                id: 'insights-site-analytics-visitors-os',
                                label: 'navigation.sideNavigation.operatingSystems',
                                route: 'base.customer.domain.statistics.visitors.os',
                            },
                            {
                                id: 'insights-site-analytics-visitors-resolutions',
                                label: 'navigation.sideNavigation.screenResolutions',
                                route: 'base.customer.domain.statistics.visitors.resolutions',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-content',
                        label: 'navigation.sideNavigation.content',
                        route: 'base.customer.domain.statistics.pages.mostPopular',
                        activeRoutes: [
                            'base.customer.domain.statistics.pages.mostPopular',
                            'base.customer.domain.statistics.pages.mostDownloads',
                            'base.customer.domain.statistics.pages.leastPopular',
                            'base.customer.domain.statistics.pages.withoutVisits',
                            'base.customer.domain.statistics.pages.documentsWithoutDownloads',
                            'base.customer.domain.statistics.pages.siteSearch',
                            'base.customer.domain.statistics.pages.events',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-content-most-popular',
                                label: 'navigation.sideNavigation.mostVisitedPages',
                                route: 'base.customer.domain.statistics.pages.mostPopular',
                            },
                            {
                                id: 'insights-site-analytics-content-least-popular',
                                label: 'navigation.sideNavigation.leastVisitedPages',
                                route: 'base.customer.domain.statistics.pages.leastPopular',
                            },
                            {
                                id: 'insights-site-analytics-content-most-downloads',
                                label: 'navigation.sideNavigation.mostDownloads',
                                route: 'base.customer.domain.statistics.pages.mostDownloads',
                            },
                            {
                                id: 'insights-site-analytics-content-without-visits',
                                label: 'navigation.sideNavigation.pagesWithNoVisits',
                                route: 'base.customer.domain.statistics.pages.withoutVisits',
                            },
                            {
                                id: 'insights-site-analytics-content-docs-without-downloads',
                                label: 'navigation.sideNavigation.documentsWithNoDownloads',
                                route: 'base.customer.domain.statistics.pages.documentsWithoutDownloads',
                            },
                            {
                                id: 'insights-site-analytics-content-events',
                                label: 'navigation.sideNavigation.events',
                                route: 'base.customer.domain.statistics.pages.events',
                            },
                            {
                                id: 'insights-site-analytics-content-site-search',
                                label: 'navigation.sideNavigation.siteSearch',
                                route: 'base.customer.domain.statistics.pages.siteSearch',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-traffic-sources',
                        label: 'navigation.sideNavigation.trafficSources',
                        route: 'base.customer.domain.statistics.sources.summary',
                        activeRoutes: [
                            'base.customer.domain.statistics.sources.summary',
                            'base.customer.domain.statistics.sources.searchEngines',
                            'base.customer.domain.statistics.sources.social',
                            'base.customer.domain.statistics.sources.domains',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-traffic-sources-summary',
                                label: 'navigation.sideNavigation.summary',
                                route: 'base.customer.domain.statistics.sources.summary',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-search-engines',
                                label: 'navigation.sideNavigation.searchEngines',
                                route: 'base.customer.domain.statistics.sources.searchEngines',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-social-networks',
                                label: 'navigation.sideNavigation.socialNetworks',
                                route: 'base.customer.domain.statistics.sources.social',
                            },
                            {
                                id: 'insights-site-analytics-traffic-sources-referring-domains',
                                label: 'navigation.sideNavigation.referringDomains',
                                route: 'base.customer.domain.statistics.sources.domains',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-analytics-entry-exit',
                        label: 'navigation.sideNavigation.entryAndExit',
                        route: 'base.customer.domain.statistics.entryExit.entry',
                        activeRoutes: [
                            'base.customer.domain.statistics.entryExit.entry',
                            'base.customer.domain.statistics.entryExit.exit',
                            'base.customer.domain.statistics.entryExit.bounceRates',
                        ],
                        children: [
                            {
                                id: 'insights-site-analytics-entry-exit-entry',
                                label: 'navigation.sideNavigation.entryPages',
                                route: 'base.customer.domain.statistics.entryExit.entry',
                            },
                            {
                                id: 'insights-site-analytics-entry-exit-exit',
                                label: 'navigation.sideNavigation.exitPages',
                                route: 'base.customer.domain.statistics.entryExit.exit',
                            },
                            {
                                id: 'insights-site-analytics-entry-exit-bounce-rates',
                                label: 'navigation.sideNavigation.bounceRates',
                                route: 'base.customer.domain.statistics.entryExit.bounceRates',
                            },
                        ],
                    },
                ],
            },
            {
                id: 'insights-scan-history',
                label: 'navigation.sideNavigation.scanHistory',
                // Nearest existing state fallback for legacy "Scan History" entry.
                route: 'base.customer.domain.changesHistory',
                visibilityKey: 'history-center',
                activeRoutes: ['base.customer.domain.changesHistory'],
            },
            {
                id: 'insights-site-inventory',
                label: 'navigation.sideNavigation.siteInventory',
                route: 'base.customer.domain.inventory.dashboard',
                visibilityKey: 'inventory-root',
                routeMatchers: ['base.customer.domain.inventory'],
                children: [
                    {
                        id: 'insights-site-inventory-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.inventory.dashboard',
                    },
                    {
                        id: 'insights-site-inventory-content',
                        label: 'navigation.sideNavigation.content',
                        route: 'base.customer.domain.inventory.content.pages',
                        activeRoutes: [
                            'base.customer.domain.inventory.content.pages',
                            'base.customer.domain.inventory.content.documents',
                            'base.customer.domain.inventory.content.images',
                            'base.customer.domain.inventory.content.inlineimages',
                            'base.customer.domain.inventory.content.links',
                        ],
                        children: [
                            {
                                id: 'insights-site-inventory-content-html-pages',
                                label: 'navigation.sideNavigation.htmlPages',
                                route: 'base.customer.domain.inventory.content.pages',
                            },
                            {
                                id: 'insights-site-inventory-content-documents',
                                label: 'navigation.sideNavigation.documents',
                                route: 'base.customer.domain.inventory.content.documents',
                            },
                            {
                                id: 'insights-site-inventory-content-images',
                                label: 'navigation.sideNavigation.images',
                                route: 'base.customer.domain.inventory.content.images',
                            },
                            {
                                id: 'insights-site-inventory-content-inline-images',
                                label: 'navigation.sideNavigation.inlineImages',
                                route: 'base.customer.domain.inventory.content.inlineimages',
                            },
                            {
                                id: 'insights-site-inventory-content-links',
                                label: 'navigation.sideNavigation.links',
                                route: 'base.customer.domain.inventory.content.links',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-inventory-technical',
                        label: 'navigation.sideNavigation.technical',
                        route: 'base.customer.domain.inventory.content.form',
                        activeRoutes: [
                            'base.customer.domain.inventory.content.form',
                            'base.customer.domain.inventory.content.headlink',
                            'base.customer.domain.inventory.content.iframe',
                            'base.customer.domain.inventory.content.frame',
                            'base.customer.domain.inventory.content.css',
                            'base.customer.domain.inventory.content.js',
                        ],
                        children: [
                            {
                                id: 'insights-site-inventory-technical-forms',
                                label: 'navigation.sideNavigation.forms',
                                route: 'base.customer.domain.inventory.content.form',
                            },
                            {
                                id: 'insights-site-inventory-technical-headlinks',
                                label: 'navigation.sideNavigation.headlinks',
                                route: 'base.customer.domain.inventory.content.headlink',
                            },
                            {
                                id: 'insights-site-inventory-technical-iframes',
                                label: 'navigation.sideNavigation.iframes',
                                route: 'base.customer.domain.inventory.content.iframe',
                            },
                            {
                                id: 'insights-site-inventory-technical-frames',
                                label: 'navigation.sideNavigation.frames',
                                route: 'base.customer.domain.inventory.content.frame',
                            },
                            {
                                id: 'insights-site-inventory-technical-css',
                                label: 'navigation.sideNavigation.css',
                                route: 'base.customer.domain.inventory.content.css',
                            },
                            {
                                id: 'insights-site-inventory-technical-javascript',
                                label: 'navigation.sideNavigation.javascript',
                                route: 'base.customer.domain.inventory.content.js',
                            },
                        ],
                    },
                    {
                        id: 'insights-site-inventory-personal',
                        label: 'navigation.sideNavigation.data',
                        route: 'base.customer.domain.inventory.email',
                        activeRoutes: ['base.customer.domain.inventory.email'],
                        children: [
                            {
                                id: 'insights-site-inventory-personal-email',
                                label: 'navigation.sideNavigation.emailAddresses',
                                route: 'base.customer.domain.inventory.email',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'site-performance',
        label: 'navigation.sideNavigation.sitePerformance',
        icon: 'sitePerformance',
        route: 'base.customer.domain.seo.summary',
        visibilityKey: 'site-performance-root',
        children: [
            {
                id: 'site-performance-seo',
                label: 'navigation.sideNavigation.seoIssues',
                route: 'base.customer.domain.seo.summary',
                visibilityKey: 'seo-root',
                activeRoutes: [
                    'base.customer.domain.seo.summary',
                    'base.customer.domain.seo.pages',
                    'base.customer.domain.seo.issues',
                ],
                children: [
                    {
                        id: 'site-performance-seo-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.seo.summary',
                    },
                    {
                        id: 'site-performance-seo-pages',
                        label: 'navigation.sideNavigation.issuesByPage',
                        route: 'base.customer.domain.seo.pages',
                    },
                    {
                        id: 'site-performance-seo-checkpoints',
                        label: 'navigation.sideNavigation.issuesByType',
                        route: 'base.customer.domain.seo.issues',
                    },
                ],
            },
            {
                id: 'site-performance-scans',
                label: 'navigation.sideNavigation.performanceScans',
                route: 'base.customer.domain.page-performance.pages',
                visibilityKey: 'performance-root',
                activeRoutes: [
                    'base.customer.domain.page_performance',
                    'base.customer.domain.page-performance.pages',
                ],
            },
            {
                id: 'insights-uptime-monitoring',
                label: 'navigation.sideNavigation.uptimeMonitoring',
                route: 'base.customer.domain.uptimes.index',
                visibilityKey: 'uptime-root',
                activeRoutes: [
                    'base.customer.domain.uptimes',
                    'base.customer.domain.uptimes.index',
                    'base.customer.domain.uptimes.show',
                ],
            },
        ],
    },
    {
        id: 'privacy',
        label: 'navigation.sideNavigation.privacy',
        icon: 'privacy',
        route: 'base.customer.domain.data-privacy.summary',
        visibilityKey: 'privacy-root',
        children: [
            {
                id: 'privacy-data',
                label: 'navigation.sideNavigation.dataPrivacy',
                route: 'base.customer.domain.data-privacy.summary',
                visibilityKey: 'data-privacy-core',
                activeRoutes: [
                    'base.customer.domain.data-privacy.summary',
                    'base.customer.domain.data-privacy.index',
                    'base.customer.domain.data-privacy.issues',
                    'base.customer.domain.data-privacy.checklist',
                    'base.customer.domain.data-privacy.domain-settings',
                ],
                children: [
                    {
                        id: 'privacy-data-summary',
                        label: 'navigation.sideNavigation.summary',
                        route: 'base.customer.domain.data-privacy.summary',
                    },
                    {
                        id: 'privacy-data-fast-track',
                        label: 'navigation.sideNavigation.fastTrack',
                        dialogParams: {
                            body: PrivacyDialogsFastTrackComponent,
                            size: 'fw',
                        },
                    },
                    {
                        id: 'privacy-data-content',
                        label: 'navigation.sideNavigation.issuesByPage',
                        route: 'base.customer.domain.data-privacy.index',
                    },
                    {
                        id: 'privacy-data-issues',
                        label: 'navigation.sideNavigation.issuesByType',
                        route: 'base.customer.domain.data-privacy.issues',
                    },
                    {
                        id: 'privacy-data-checklist',
                        label: 'navigation.sideNavigation.checklist',
                        route: 'base.customer.domain.data-privacy.checklist',
                    },
                ],
            },
            {
                id: 'privacy-consent-overview',
                label: 'navigation.sideNavigation.cookieConsentManager',
                route: 'base.customer.domain.data-privacy.consent-overview',
                visibilityKey: 'consent-manager-root',
                activeRoutes: [
                    'base.customer.domain.data-privacy.consent-overview',
                    'base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate',
                    'base.customer.domain.data-privacy.consent-overview.consent-log',
                    'base.customer.domain.data-privacy.consent-overview.cookie-overview',
                ],
                children: [
                    {
                        id: 'privacy-consent-cookie-acceptance',
                        label: 'navigation.sideNavigation.acceptanceRates',
                        route: 'base.customer.domain.data-privacy.consent-overview.cookie-acceptance-rate',
                    },
                    {
                        id: 'privacy-consent-log',
                        label: 'navigation.sideNavigation.consentLog',
                        route: 'base.customer.domain.data-privacy.consent-overview.consent-log',
                    },
                    {
                        id: 'privacy-consent-cookie-overview',
                        label: 'navigation.sideNavigation.cookieOverview',
                        route: 'base.customer.domain.data-privacy.consent-overview.cookie-overview',
                        visibilityKey: 'consent-manager-cookie-overview',
                    },
                ],
            },
        ],
    },
];

export const ADMIN_SIDE_NAV_ITEMS: SideNavigationItem[] = [
    {
        id: 'admin-dashboard',
        label: 'navigation.sideNavigation.dashboard',
        icon: 'home',
        route: 'base.customer.admin.dashboard',
        activeRoutes: ['base.customer.admin.dashboard'],
    },
    {
        id: 'admin-user-management',
        label: 'navigation.sideNavigation.userManagement',
        icon: 'group',
        children: [
            {
                id: 'admin-users',
                label: 'navigation.sideNavigation.users',
                route: 'base.customer.admin.users',
                activeRoutes: [
                    'base.customer.admin.users',
                    'base.customer.admin.users.index',
                    'base.customer.admin.add-user',
                    'base.customer.admin.edit-user',
                ],
            },
            {
                id: 'admin-audit-log',
                label: 'navigation.sideNavigation.auditLog',
                route: 'base.customer.admin.audit-log',
                visibilityKey: 'admin-audit-log',
                activeRoutes: [
                    'base.customer.admin.audit-log',
                    'base.customer.admin.auditlog',
                    'base.customer.admin.auditlog.index',
                ],
            },
        ],
    },
    {
        id: 'admin-configuration',
        label: 'navigation.sideNavigation.configuration',
        icon: 'build',
        children: [
            {
                id: 'admin-labels',
                label: 'navigation.sideNavigation.labels',
                route: 'base.customer.admin.labels',
                visibilityKey: 'admin-labels',
                activeRoutes: [
                    'base.customer.admin.labels',
                    'base.customer.admin.labels.index',
                ],
            },
            {
                id: 'admin-performance-profiles',
                label: 'navigation.sideNavigation.performanceProfiles',
                route: 'base.customer.admin.performance-profiles',
                visibilityKey: 'admin-page-performance',
                activeRoutes: [
                    'base.customer.admin.performance-profiles',
                    'base.customer.admin.performance-profile-form',
                ],
            },
            {
                id: 'admin-scan-exclusions',
                label: 'navigation.sideNavigation.scanExclusions',
                route: 'base.customer.admin.scan-exclusions',
                visibilityKey: 'admin-source-code-excludes',
                activeRoutes: [
                    'base.customer.admin.scan_exclusions',
                    'base.customer.admin.add-scan-exclusions',
                    'base.customer.admin.edit-scan-exclusions',
                    'base.customer.admin.source_code_excludes',
                    'base.customer.admin.source-code-excludes.index',
                ],
            },
        ],
    },
    {
        id: 'admin-integrations',
        label: 'navigation.sideNavigation.integrations',
        icon: 'extension',
        children: [
            {
                id: 'admin-api-users',
                label: 'navigation.sideNavigation.apiTokens',
                route: 'base.customer.admin.api-users',
                visibilityKey: 'admin-api-users',
                activeRoutes: [
                    'base.customer.admin.api_users',
                    'base.customer.admin.api-users.index',
                ],
            },
            {
                id: 'admin-jira-connector',
                label: 'navigation.sideNavigation.jiraConnector',
                route: 'base.customer.admin.jira-connector',
                visibilityKey: 'admin-jira-connector',
                activeRoutes: ['base.customer.admin.jira-connector'],
            },
        ],
    },
];
